import { apiClient, handleApiError } from './client';
import type { ServiceRequest } from '@om/shared-types';

type ApiEnvelope<T> = {
  success?: boolean;
  data?: T;
  error?: string;
  message?: string;
};

const unwrap = <T>(payload: unknown, fallback: string): T => {
  if (payload && typeof payload === 'object' && 'success' in (payload as Record<string, unknown>)) {
    const envelope = payload as ApiEnvelope<T>;
    if (envelope.success) return envelope.data as T;
    const msg =
      (typeof envelope.error === 'string' && envelope.error.trim()) ||
      (typeof envelope.message === 'string' && envelope.message.trim()) ||
      fallback;
    throw new Error(msg);
  }
  return payload as T;
};

export interface VendorSearchResult {
  vendor_id: number;
  business_name: string;
  slug: string;
  city: string | null;
  state: string | null;
  country: string;
  rating: number;
  review_count: number;
  is_verified: boolean;
  logo_url: string | null;
  category: string;
  service_title: string | null;
}

export interface PostToVendorsRequest {
  draft_id: number;
  vendor_ids: number[];
  mode: 'selected' | 'direct';
  event_ids?: number[];
}

export interface PostOpenRequest {
  draft_id: number;
  category: string;
  event_ids?: number[];
}

export interface VendorSearchParams {
  category?: string;
  city?: string;
  min_rating?: number;
  q?: string;
}

class PostingApiService {
  async searchVendors(params: VendorSearchParams): Promise<VendorSearchResult[]> {
    try {
      const response = await apiClient.get('/vendor-search', { params });
      const payload = unwrap<VendorSearchResult[]>(response.data, 'Failed to search vendors');
      return Array.isArray(payload) ? payload : [];
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async postToVendors(celebrationId: number, body: PostToVendorsRequest): Promise<ServiceRequest[]> {
    try {
      const response = await apiClient.post(`/celebrations/${celebrationId}/service-requests/post`, body);
      const payload = unwrap<ServiceRequest[]>(response.data, 'Failed to post to vendors');
      return Array.isArray(payload) ? payload : [];
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async postOpen(celebrationId: number, body: PostOpenRequest): Promise<ServiceRequest[]> {
    try {
      const response = await apiClient.post(`/celebrations/${celebrationId}/service-requests/open`, body);
      const payload = unwrap<ServiceRequest[]>(response.data, 'Failed to post open');
      return Array.isArray(payload) ? payload : [];
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }
}

export const postingApiService = new PostingApiService();
